import React, { useEffect } from "react"
import { observer } from "mobx-react-lite"

import BaseModal from "@components/modals/components/BaseModal/BaseModal"
import ModalTitle from "@components/modals/components/ModalTitle/ModalTitle"
import useModal from "@components/modals/useModal"
import Button from "@components/ui/Button/Button"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import Text from "@components/ui/Typography/Text"
import Templates from "@components/ui/Templates"
import { TabsCollection } from "@components/ui/TabsCollection"
import { KibanaConnector } from "@framework/types/content-manager"
import { DocumentIconType, Option } from "@framework/types/utils"
import { IconName } from "@components/ui/Icon/IconName"
import Chip from "@components/ui/Chip/Chip"
import { capitalizeFirstLetter } from "@utils/textUtils"

import { ModalsTypes } from "../constants"
import FolderMappingsTab from "./FolderMappingsTab"

import styles from "./ConnectorDetailModal.module.sass"

export type ConnectorDetailModalProps = {
  connector: KibanaConnector
  icon?: DocumentIconType | IconName
}

const tabs: Option[] = [
  { name: "Overview", value: "overview" },
  { name: "Folder mappings", value: "folder-mappings" },
]

const ConnectorDetailModal: React.FC<ConnectorDetailModalProps> = observer(
  ({ connector, icon }) => {
    const modal = useModal(ModalsTypes.CONNECTOR_DETAIL_MODAL)
    const [selectedTab, setSelectedTab] = React.useState(tabs[0].name)

    useEffect(() => {
      setSelectedTab(tabs[0].name)
    }, [connector.id])

    return (
      <BaseModal
        className={styles.root}
        containerClassName={styles.container}
        title={
          <ModalTitle
            titleText={
              <div className={styles.titleContainer}>
                {icon && <DocumentIcon icon={icon} />}
                <span>{connector.name}</span>
              </div>
            }
          />
        }
      >
        <Templates.RollScript
          footerSocket={
            <div className={styles.footer}>
              <Button variant="outlined" size="big" onClick={modal.hideModal}>
                Close
              </Button>
            </div>
          }
        >
          <TabsCollection
            items={tabs}
            defaultValue={selectedTab}
            onSelect={setSelectedTab}
          />

          {selectedTab === "Overview" && (
            <div className={styles.overview}>
              <div className={styles.overviewRow}>
                <Text variant="body2">Status</Text>
                <Chip variant="rounded">{capitalizeFirstLetter(connector.status)}</Chip>
              </div>
              <div className={styles.overviewRow}>
                <Text variant="body2">Domain</Text>
                <Text variant="body1">{connector.domain || "—"}</Text>
              </div>
              <div className={styles.overviewRow}>
                <Text variant="body2">Manufacturers</Text>
                <Text variant="body1">
                  {connector.companies?.map((company) => company.name).join(", ") || "—"}
                </Text>
              </div>
              <div className={styles.overviewRow}>
                <Text variant="body2">Content count</Text>
                <Text variant="body1">{connector.contentCount ?? 0}</Text>
              </div>
              <Text variant="caption1" className={styles.hint}>
                Folder mappings classify documents by SharePoint URL path and assign
                them to a manufacturer during sync.
              </Text>
            </div>
          )}

          {selectedTab === "Folder mappings" && (
            <FolderMappingsTab connectorId={connector.id} />
          )}
        </Templates.RollScript>
      </BaseModal>
    )
  }
)

export default ConnectorDetailModal
